import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../services/api';
import useCartStore from '../store/cartStore';
import Loading from '../components/common/Loading';
import { toast } from 'react-hot-toast';
import iraqProvinces from '../data/iraq_provinces.json';

const checkoutSchema = z.object({
  customer_name: z.string().min(3, 'الاسم يجب أن يكون 3 أحرف على الأقل'),
  customer_phone: z.string().min(10, 'رقم الهاتف غير صحيح'),
  customer_email: z.string().email('البريد الإلكتروني غير صحيح').optional().or(z.literal('')),
  province: z.string().min(1, 'يرجى اختيار المحافظة'),
  district: z.string().min(1, 'يرجى اختيار القضاء'),
  address: z.string().min(10, 'العنوان يجب أن يكون 10 أحرف على الأقل'),
  preferred_delivery_date: z.string().optional(),
  preferred_delivery_time: z.string().optional(),
  notes: z.string().optional(),
});

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, clearCart, getTotal, getItemsByStore } = useCartStore();
  const [submitting, setSubmitting] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [createdOrders, setCreatedOrders] = useState([]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: zodResolver(checkoutSchema),
  });

  const province = watch('province');

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
    }
  }, [items, navigate]);

  useEffect(() => {
    if (province) {
      setValue('district', '');
      setSelectedProvince(province);
    }
  }, [province, setValue]);

  const getDistricts = () => {
    if (!selectedProvince) return [];
    const provinceData = iraqProvinces.find(p => p.province_ar === selectedProvince);
    return provinceData ? provinceData.districts : [];
  };

  const getName = (product) => {
    const lang = i18n.language;
    if (lang === 'ar' && product.name_ar) return product.name_ar;
    if (lang === 'en' && product.name_en) return product.name_en;
    if (lang === 'ku' && product.name_ku) return product.name_ku;
    return product.name;
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    const orders = [];
    const itemsByStore = getItemsByStore();
    
    try {
      // Create separate order for each store
      for (const [storeSlug, storeData] of Object.entries(itemsByStore)) {
        const orderData = {
          ...data,
          store: storeSlug,
          items: storeData.items.map(item => ({
            product: item.product.id,
            quantity: item.quantity,
            price: item.product.price
          }))
        };
        
        const res = await api.post('/orders/', orderData);
        orders.push({
          orderNumber: res.data.order_number,
          storeName: storeData.storeName,
          storeSlug: storeSlug
        });
      }
      
      setCreatedOrders(orders);
      
      // Show success message
      if (orders.length > 1) {
        toast.success(`تم إنشاء ${orders.length} طلبات بنجاح!`);
      } else {
        toast.success('تم إنشاء الطلب بنجاح!');
      }
      
      clearCart();
      
      // Navigate to tracking page with first order
      navigate(`/orders/track?order_number=${orders[0].orderNumber}`);
      
    } catch (error) {
      console.error('Error creating orders:', error);
      toast.error('حدث خطأ في إنشاء الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const itemsByStore = getItemsByStore();
  const storeCount = Object.keys(itemsByStore).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">{t('checkout.customer_info')}</h1>
        
        {storeCount > 1 && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-yellow-900 mb-2">⚠️ ملاحظة مهمة</h3>
            <p className="text-yellow-800">
              لديك منتجات من {storeCount} متاجر مختلفة. سيتم إنشاء طلب منفصل لكل متجر باستخدام نفس معلومات التوصيل.
            </p>
            <div className="mt-3 space-y-1">
              {Object.entries(itemsByStore).map(([slug, store]) => (
                <div key={slug} className="text-sm text-yellow-700">
                  • {store.storeName}: {store.items.length} منتج - {store.items.reduce((total, item) => 
                    total + (item.product.price * item.quantity), 0
                  ).toLocaleString()} د.ع
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-2xl shadow-md">
              {/* Customer Information */}
              <h2 className="text-2xl font-bold mb-6 text-gray-900">{t('checkout.customer_info')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.name')} *
                  </label>
                  <input
                    {...register('customer_name')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="الاسم الكامل"
                  />
                  {errors.customer_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.customer_name.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.phone')} *
                  </label>
                  <input
                    {...register('customer_phone')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="07XXXXXXXXX"
                  />
                  {errors.customer_phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.customer_phone.message}</p>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block mb-2 font-semibold text-gray-900">
                  {t('checkout.email')}
                </label>
                <input
                  {...register('customer_email')}
                  type="email"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="example@email.com"
                />
                {errors.customer_email && (
                  <p className="text-red-500 text-sm mt-1">{errors.customer_email.message}</p>
                )}
              </div>
              
              {/* Shipping Information */}
              <h2 className="text-2xl font-bold mb-6 mt-8 text-gray-900">{t('checkout.shipping_info')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.province')} *
                  </label>
                  <select
                    {...register('province')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">اختر المحافظة</option>
                    {iraqProvinces.map((province, index) => (
                      <option key={index} value={province.province_ar}>
                        {province.province_ar}
                      </option>
                    ))}
                  </select>
                  {errors.province && (
                    <p className="text-red-500 text-sm mt-1">{errors.province.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.district')} *
                  </label>
                  <select
                    {...register('district')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    disabled={!selectedProvince}
                  >
                    <option value="">اختر القضاء</option>
                    {getDistricts().map((district, index) => (
                      <option key={index} value={district.ar}>
                        {district.ar}
                      </option>
                    ))}
                  </select>
                  {errors.district && (
                    <p className="text-red-500 text-sm mt-1">{errors.district.message}</p>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block mb-2 font-semibold text-gray-900">
                  {t('checkout.address')} *
                </label>
                <textarea
                  {...register('address')}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  placeholder="العنوان التفصيلي..."
                />
                {errors.address && (
                  <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>
                )}
              </div>
              
              {/* Delivery Preferences */}
              <h2 className="text-xl font-bold mb-4 mt-8 text-gray-900">{t('checkout.delivery_preferences')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.preferred_date')}
                  </label>
                  <input
                    {...register('preferred_delivery_date')}
                    type="date"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    {t('checkout.preferred_time')}
                  </label>
                  <select
                    {...register('preferred_delivery_time')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">اختر الوقت</option>
                    <option value="morning">صباحاً (8:00 - 12:00)</option>
                    <option value="afternoon">ظهراً (12:00 - 16:00)</option>
                    <option value="evening">مساءً (16:00 - 20:00)</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block mb-2 font-semibold text-gray-900">
                  {t('checkout.notes')}
                </label>
                <textarea
                  {...register('notes')}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  placeholder="ملاحظات إضافية..."
                />
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    جاري إنشاء الطلب...
                  </span>
                ) : (
                  t('checkout.place_order') || 'تأكيد الطلب'
                )}
              </button>
            </form>
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-md sticky top-24">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">ملخص الطلب</h2>
              
              {/* Grouped by store */}
              {Object.entries(getItemsByStore()).map(([storeSlug, storeData]) => (
                <div key={storeSlug} className="mb-6 pb-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    🏪 {storeData.storeName}
                  </h3>
                  <div className="space-y-2 mb-3">
                    {storeData.items.map((item) => (
                      <div key={item.product.id} className="flex justify-between text-sm">
                        <span className="text-gray-700">{getName(item.product)} x{item.quantity}</span>
                        <span className="font-semibold text-gray-900">
                          {(item.product.price * item.quantity).toLocaleString()} د.ع
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-600">مجموع المتجر:</span>
                    <span className="text-blue-600">
                      {storeData.items.reduce((total, item) => 
                        total + (item.product.price * item.quantity), 0
                      ).toLocaleString()} د.ع
                    </span>
                  </div>
                </div>
              ))}
              
              <div className="space-y-4">
                <div className="flex justify-between text-gray-600">
                  <span>المجموع الفرعي</span>
                  <span className="font-semibold text-gray-900">{getTotal().toLocaleString()} د.ع</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>الشحن</span>
                  <span className="font-semibold text-gray-900">0 د.ع</span>
                </div>
                <div className="border-t-2 border-gray-200 pt-4 flex justify-between text-2xl font-bold">
                  <span className="text-gray-900">الإجمالي</span>
                  <span className="text-blue-600">{getTotal().toLocaleString()} د.ع</span>
                </div>
              </div>
              
              {storeCount > 1 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    سيتم إنشاء {storeCount} طلبات منفصلة
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
